//
// social-index/controllers
//

import {pageview} from '../../../core/js/tracking';
import * as collection_classlist from '../../../core/js/helpers/collection-classlist';
import fetch_index from './fetch-index';

const helpers = require('./helpers');


// index_selector_list - sends events when a group or sector is selected
// ----------------------------
// responds to:
//    - change on si group select
//    - click on index list
//
// sends:
//    - si/set-group    detail: { group_name: x }
//    - si/set-index    detail: { index_name: x }
//

const index_selector_list = (function() {
  const el_selector_wrap = document.querySelector('.js-si-selector');
  const e = {
    group_select: el_selector_wrap.getElementsByTagName('select')[0],
    index_list:   el_selector_wrap.querySelector('.js-si-selector-list'),
  };

  function init() {
    e.group_select.addEventListener('change', cb_group_select_change);
    e.index_list.addEventListener('click', cb_index_item_click);
  }
  init();

  function cb_group_select_change(ev) {
    const group_name = e.group_select.value;
    window.dispatchEvent(new CustomEvent('si/set-group', {detail: {group_name}}));

    const indexes = helpers.indexes_for_group(group_name);
    if (indexes && indexes.length) {
      const index_name = indexes[0]['name'];
      window.dispatchEvent(new CustomEvent('si/set-index', {detail: {index_name}}));
    }
  }

  function cb_index_item_click(ev) {
    if (ev.target.classList.contains('js-si-selector-list-item')) {
      const index_name = ev.target.textContent.trim();
      window.dispatchEvent(new CustomEvent('si/set-index', {detail: {index_name}}));
    }
  }
})();



// si_load_controller - orchestrates loading & rendering of indexes
// ----------------------------
// responds to:
//    - si/set-index    - begins load & sends below events during the process
// sends:
//    - si/load-begin
//    - si/load-finish  : data
//    - si/load-error   : { reason: nonexistent / ajax error, index_name: Airlines }
//

const si_load_controller = (function() {
  let current_index_name;
  let load_in_progress;

  function init() {
    window.addEventListener('si/set-index', cb_set);
  }
  init();

  function cb_set(ev) {
    const index_name = ev.detail.index_name;

    // Prevent reloading of same index
    if (index_name === current_index_name) {
      return;
    }

    current_index_name = index_name;

    if (!load_in_progress) {
      do_load(index_name);
    }
  }

  function do_load(index_name) {
    load_in_progress = true;

    window.dispatchEvent(new CustomEvent('si/load-begin'));

    fetch_index(index_name)
      .then(function do_load__success(data) {
        load_in_progress = false;
        window.dispatchEvent(new CustomEvent('si/load-finish', {detail: data}));
      })
      .catch(function do_load__error(err) {
        load_in_progress = false;

        console.log('si_load_controller/set(): load error', err.message);

        window.dispatchEvent(new CustomEvent('si/load-error', {
          detail: {
            reason: err.message,
            index_name,
          },
        }));
      });
  }
})();



// sort_controller - bind sort clicks, update header element state, send si/sort events
// ----------------------------
// Responds to:
//    - click on js-si-header-sort-col header elements - sends si/sort, updates header
//                                                       elements' state
//    - si/load-begin                                  - resets internal sort state, header
//                                                       element state
// Sends:
//    - si/sort         sort should occur   detail: { field_index: ~, direction: ~ }
//
// Hooks:
//    - js-si-header-sort-col:         header column items that can be clicked to trigger sort
//    - js-si-header-sort-col-title:   element within in the header column item wrapping the title
//    - js-si-data-col--total

const sort_controller = (function() {
  let header_items;
  let cur_sort_header;
  let cur_sort_direction = helpers.SORT_DESC;

  function init() {
    header_items = Array.from(document.getElementsByClassName('js-si-header-sort-col'));
    header_items.forEach((item) => item.addEventListener('click', cb_sortby));

    window.addEventListener('si/load-begin', reset);
  }
  init();

  function get_total_sort_header_el() {
    return header_items.find(item => item.classList.contains('js-si-data-col--total'));
  }

  function cb_sortby(ev) {
    const el_sort_header = ev.currentTarget;

    const is_different_element = el_sort_header !== cur_sort_header;
    cur_sort_header = el_sort_header;

    if (is_different_element) {
      cur_sort_direction = helpers.SORT_DESC;
    }

    else {
      cur_sort_direction = helpers.invert_sort(cur_sort_direction);
    }

    update_sort();
  }

  function update_sort() {
    update_sort_header_attribs();

    const i = header_items.indexOf(cur_sort_header);
    window.dispatchEvent(new CustomEvent('si/sort', {
      detail: {
        field_index: i,
        direction:   cur_sort_direction,
      }
    }));
  }

  function update_sort_header_attribs() {
    const sort_class = cur_sort_direction == helpers.SORT_ASC ? 's-asc' : 's-desc';

    header_items.forEach((item) => {
      item.classList.remove('s-asc');
      item.classList.remove('s-desc');
    });

    cur_sort_header.classList.add(sort_class);
  }

  function reset() {
    const sort_header = get_total_sort_header_el();
    const sort_direction = helpers.SORT_DESC;

    if (sort_header === undefined) {
      return;
    }

    if (sort_header !== cur_sort_header || sort_direction !== cur_sort_direction) {
      cur_sort_header = sort_header;
      cur_sort_direction = sort_direction;
    }

    update_sort_header_attribs();
  }
})();



// url_and_initial_load_controller
// ----------------------------
// - handles load of initially displayed index
// - and dynamic url updates on index selection by index_selector_list
// - because this one does first load, it runs after the others
//   to give them a chance to init
//
// responds to:
//    - si/set-index   - pushes a history state (updates the url)
//    - popstate       - pops the history stack
// sends:
//    - si/set-group   detail: { group_name: y }
//    - si/set-index   detail: { index_name: x,  page_load: bool }
//

const url_and_initial_load_controller = (function() {
  function init() {
    window.addEventListener('si/set-index', cb_set);
    window.addEventListener('popstate', cb_pop);

    const firstload_index = index_slug_for_url();
    set_index(firstload_index, true);
  }
  init();

  function index_slug_for_url() {
    const exp_path = 'brandwatch-index';
    const i = location.pathname.indexOf(exp_path);
    if (i === -1) {
      return null;
    }

    const slug = (function() {
      let slug = location.pathname.substr(i + exp_path.length);
      slug = slug.replace(/\//g, '');
      slug = decodeURIComponent(slug);
      return slug;
    })();

    return slug === '' ? window.social_index_default_index : slug;
  }

  function set_index(index_name_or_url_component, is_first_load) {
    const index_name = helpers.find_index_prop(index_name_or_url_component, 'name') ||
      window.social_index_default_index;

    const group_name = helpers.find_index_prop(index_name, 'group-name');

    window.dispatchEvent(new CustomEvent('si/set-group', {
      detail: { group_name },
    }));

    window.dispatchEvent(new CustomEvent('si/set-index', {
      detail: {
        index_name,
        page_load: !!is_first_load,
        from_url_controller: true,
      },
    }));
  }

  function cb_set(ev) {
    if (ev.detail.from_url_controller) {
      if (ev.detail.page_load) {
        set_url(ev.detail.index_name, true);
      }
      return;
    }

    set_url(ev.detail.index_name);
  }

  function set_url(index_name, replace_current_state) {
    const base_path = document.location.pathname.replace(/brandwatch-index.*/, 'brandwatch-index');
    const url_part = helpers.find_index_prop(index_name, 'slug');
    const path = `${base_path}/${url_part}`;

    if (replace_current_state) {
      history.replaceState({index_name}, '', path);
    }
    else {
      history.pushState({index_name}, '', path);
    }

    pageview(path);
  }

  function cb_pop(ev) {
    let index_name = index_slug_for_url();
    set_index(index_name);
  }
})();



// index_load_effects_controller
// ----------------------------
// - handles the visual animation when switching between index
// - handles initial smooth load effects of the page
//
// Hooks:
//   - js-si-smooth-load:  apply to element to fade in on initial load
//   – js-si-panel:        outermost container of the index, used to fade when switching indexes
//   – js-si-panel__slide: used for slide in effect after index load. must not be the same
//                         element as js-si-panel
//   – js-si-loader:       loading spinner icon, shows status of load


const index_load_effects_controller = (function() {
  const index_loader           = document.querySelector('.js-si-loader');
  const index_main_panel       = document.querySelector('.js-si-panel');
  const index_main_panel_slide = document.querySelector('.js-si-panel__slide');
  const smooth_load_coll       = document.getElementsByClassName('js-si-smooth-load');

  // Remove state on animation completion
  helpers.cb_after_anim(index_main_panel_slide, disp__disable_slide);

  function disp__disable_slide() {
    index_main_panel_slide.classList.remove('s-animated');
  }

  function disp__enable_slide() {
    index_main_panel_slide.classList.add('s-animated');
  }

  function disp__set_loading() {
    index_main_panel.classList.remove('s-index-loaded');
    index_loader.classList.add('s-show');

    disp__disable_slide();
  }

  function disp__finish_loading() {
    disp__enable_slide();

    index_main_panel.classList.add('s-index-loaded');
    index_loader.classList.remove('s-show');
  }

  function init() {
    window.addEventListener('si/load-begin', disp__set_loading);
    window.addEventListener('si/load-finish', disp__finish_loading);
    window.addEventListener('si/load-error', disp__finish_loading);

    index_main_panel_slide.classList.add('s-restore-flow');
    index_main_panel_slide.classList.add('s-animated');
    index_main_panel_slide.classList.add('s-page-loaded');

		collection_classlist.add(smooth_load_coll, 's-page-loaded');
  }
  init();
})();
